import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, AlertCircle, ArrowRight, Loader } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import aural_logo from '../assets/images/aural_ico.png';


export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const { addToast } = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false });
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);
  useEffect(() => { if (isAuthenticated) navigate('/dashboard', { replace: true }); }, [isAuthenticated, navigate]);

  const emailErr = touched.email && !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  const passwordErr = touched.password && password.length < 6;
  const canSubmit = email && password && !emailErr && !passwordErr;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched({ email: true, password: true });
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      addToast('Welcome back! Signed in successfully.', 'success');
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function fillDemo() { setEmail('admin@auralglobal.com'); setPassword('aural2025'); setError(''); }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', background: 'var(--bg)',
      fontFamily: "'Inter', sans-serif",
      opacity: mounted ? 1 : 0, transition: 'opacity 0.4s ease',
    }}>

      {/* ── LEFT PANEL ── */}
      <div style={{
        flex: '0 0 480px', background: 'var(--surface)',
        borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
        padding: '48px',
        position: 'relative', overflow: 'hidden',
      }} className="login-left">

        {/* Decorative orbs */}
        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(191,61,12,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(191,61,12,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 'auto', position: 'relative', zIndex: 1 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, color: '#fff', fontFamily: "'Playfair Display', serif",
            fontStyle: 'italic', fontWeight: 400,
          }}>
            <img src={aural_logo} alt="Aurel Global" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.3px' }}>Aurel Global</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 400 }}>Admin Dashboard</div>
          </div>
        </div>

        {/* Hero text */}
        <div style={{ position: 'relative', zIndex: 1, paddingBottom: 48 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'var(--or-dim)', border: '1px solid rgba(191,61,12,0.25)',
            borderRadius: 100, padding: '5px 12px', marginBottom: 24,
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--or)', animation: 'pulse-ring 2s ease-in-out infinite' }} />
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--or)', letterSpacing: '0.5px' }}>SECURE ADMIN ACCESS</span>
          </div>

          <h1 style={{
            fontFamily: "'Playfair Display', serif", fontSize: 38, fontWeight: 400,
            color: 'var(--text)', lineHeight: 1.2, marginBottom: 16, letterSpacing: '-0.5px',
          }}>
            Manage your<br />
            <em style={{ color: 'var(--or)' }}>content & assets</em><br />
            with ease.
          </h1>

          <p style={{ fontSize: 14, fontWeight: 300, color: 'var(--text2)', lineHeight: 1.75, maxWidth: 320 }}>
            Full control over website content, certifications, documents, and media — all from one secure dashboard.
          </p>

          {/* Feature list */}
          <div style={{ marginTop: 36, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { icon: '📝', text: 'Edit website content in real-time' },
              { icon: '📁', text: 'Upload & manage PDF documents' },
              { icon: '🔒', text: 'Role-based secure access control' },
              { icon: '⚡', text: 'Changes reflect instantly live' },
            ].map(f => (
              <div key={f.text} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 14 }}>{f.icon}</span>
                <span style={{ fontSize: 14, fontWeight: 300, color: 'var(--text2)' }}>{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ fontSize: 12, color: 'var(--text3)', position: 'relative', zIndex: 1 }}>
          © 2025 Aurel Global Pvt. Ltd. · All rights reserved
        </div>
      </div>

      {/* ── RIGHT PANEL — FORM ── */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '48px 24px',
      }}>
        <div style={{ width: '100%', maxWidth: 420 }} className="slide-up">

          {/* Form header */}
          <div style={{ marginBottom: 36 }}>
            <h2 style={{ fontSize: 26, fontWeight: 600, color: 'var(--text)', marginBottom: 8, letterSpacing: '-0.4px' }}>
              Sign in to dashboard
            </h2>
            <p style={{ fontSize: 14, fontWeight: 300, color: 'var(--text2)' }}>
              Enter your credentials to access the admin panel.
            </p>
          </div>

          {/* Error alert */}
          {error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
              background: 'var(--red-d)', border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 10, marginBottom: 24,
              animation: 'slideUp 0.25s ease both',
            }}>
              <AlertCircle size={15} color="var(--red)" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 14, color: 'var(--red)', fontWeight: 400 }}>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Email */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text2)', marginBottom: 8, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                Email Address
              </label>
              <div style={{ position: 'relative' }}>
                <Mail size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: emailErr ? 'var(--red)' : 'var(--text3)', pointerEvents: 'none' }} />
                <input
                  type="email" value={email} autoComplete="email" autoFocus
                  onChange={e => setEmail(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, email: true }))}
                  placeholder="admin@auralglobal.com"
                  style={{
                    width: '100%', padding: '13px 14px 13px 42px',
                    background: 'var(--card)', border: `1px solid ${emailErr ? 'var(--red)' : 'var(--border2)'}`,
                    borderRadius: 10, color: 'var(--text)', fontSize: 14, fontWeight: 300,
                    fontFamily: "'Inter', sans-serif", outline: 'none', transition: 'border-color 0.2s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={e => { if (!emailErr) e.currentTarget.style.borderColor = 'var(--or)'; }}
                  onBlurCapture={e => { e.currentTarget.style.borderColor = emailErr ? 'var(--red)' : 'var(--border2)'; }}
                />
              </div>
              {emailErr && <p style={{ fontSize: 13, color: 'var(--red)', marginTop: 6 }}>Enter a valid email address</p>}
            </div>

            {/* Password */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text2)', letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                  Password
                </label>
              </div>
              <div style={{ position: 'relative' }}>
                <Lock size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: passwordErr ? 'var(--red)' : 'var(--text3)', pointerEvents: 'none' }} />
                <input
                  type={showPw ? 'text' : 'password'} value={password} autoComplete="current-password"
                  onChange={e => setPassword(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, password: true }))}
                  placeholder="Enter your password"
                  style={{
                    width: '100%', padding: '13px 44px 13px 42px',
                    background: 'var(--card)', border: `1px solid ${passwordErr ? 'var(--red)' : 'var(--border2)'}`,
                    borderRadius: 10, color: 'var(--text)', fontSize: 14, fontWeight: 300,
                    fontFamily: "'Inter', sans-serif", outline: 'none', transition: 'border-color 0.2s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={e => { if (!passwordErr) e.currentTarget.style.borderColor = 'var(--or)'; }}
                  onBlurCapture={e => { e.currentTarget.style.borderColor = passwordErr ? 'var(--red)' : 'var(--border2)'; }}
                />
                <button type="button" onClick={() => setShowPw(v => !v)} style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text3)', display: 'flex', alignItems: 'center', padding: 4,
                  borderRadius: 4, transition: 'color 0.15s',
                }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'var(--text)')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--text3)')}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                >
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {passwordErr && <p style={{ fontSize: 13, color: 'var(--red)', marginTop: 6 }}>Password must be at least 6 characters</p>}
            </div>

            {/* Submit */}
            <button
              type="submit" disabled={loading}
              style={{
                width: '100%', padding: '14px 24px',
                background: loading ? 'rgba(191,61,12,0.6)' : 'var(--or)',
                border: 'none', borderRadius: 10, color: '#fff',
                fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: "'Inter', sans-serif", letterSpacing: '0.2px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'background 0.2s, transform 0.15s, box-shadow 0.2s',
                boxShadow: loading ? 'none' : '0 4px 16px rgba(191,61,12,0.3)',
              }}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = 'var(--or2)'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(191,61,12,0.4)'; } }}
              onMouseLeave={e => { e.currentTarget.style.background = loading ? 'rgba(191,61,12,0.6)' : 'var(--or)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = loading ? 'none' : '0 4px 16px rgba(191,61,12,0.3)'; }}
            >
              {loading
                ? <><Loader size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> Signing in…</>
                : <><ArrowRight size={15} /> Sign in to Dashboard</>
              }
            </button>
          </form>

          {/* Demo credentials */}
          {/* <div style={{
            marginTop: 28, padding: '16px', background: 'var(--card)',
            border: '1px solid var(--border2)', borderRadius: 10,
          }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text3)', marginBottom: 10, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
              Demo Credentials
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
              {[
                { label: 'Email', value: 'admin@auralglobal.com' },
                { label: 'Password', value: 'aural2025' },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', gap: 8 }}>
                  <span style={{ fontSize: 13, color: 'var(--text3)', width: 60, flexShrink: 0 }}>{label}:</span>
                  <code style={{ fontSize: 13, color: 'var(--or)', background: 'var(--or-dim)', padding: '2px 6px', borderRadius: 4 }}>{value}</code>
                </div>
              ))}
            </div>
            <button onClick={fillDemo} style={{
              fontSize: 13, fontWeight: 500, color: 'var(--or)',
              background: 'var(--or-dim)', border: '1px solid rgba(191,61,12,0.25)',
              borderRadius: 7, padding: '7px 14px', cursor: 'pointer',
              fontFamily: "'Inter', sans-serif", transition: 'all 0.15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(191,61,12,0.25)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--or-dim)'; }}
            >
              Fill credentials automatically →
            </button>
          </div> */}
        </div>
      </div>

      {/* Mobile responsive — hide left panel */}
      <style>{`
        @media (max-width: 768px) {
          .login-left { display: none !important; }
        }
      `}</style>
    </div>
  );
}
